import SpinWheel from "../../../../components/SpinWheel/SpinWheel";
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { createGameHub } from "../../../api/hub";
import { Campaign } from "../../../models/campaign";
import agent from "../../../api/agent";
import CenteredCard from "../../../layout/CenteredCard";
import { env } from "../../../../app/utils/env";
import Puzzle from "../../../../components/Puzzle/Puzzle";
import { ModalConfig } from "../../../models/modalConfig";
import ScratchAndWin from "../../../../components/ScratchAndWin/ScratchAndWin";
import MatchWin from "../../../../components/MatchWin/MatchWin";
import Swal from "sweetalert2";
import Quiz from "../../../../components/Quiz/Quiz";
import { pxToVw } from "../../../../app/utils/pixeltoVw";
import CatchGame from "../../../../components/CatchGame/CatchGame";

type StartPhase = "remoteWaiting" | "classicStart" | "playing";

export default function ClassicGameBoard() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [startPhase, setStartPhase] = useState<StartPhase>("playing");
  const [remotePlayer, setRemotePlayer] = useState<any | null>(null);
  const hubRef = useRef<signalR.HubConnection | null>(null);
  const remoteTimeoutRef = useRef<number | null>(null);

  const resolveAssetUrl = (value?: string | null) =>
    value ? (value.startsWith("http") ? value : env + value) : "";

  const getInitialStartPhase = (currentCampaign: Campaign): StartPhase => {
    if (currentCampaign.collectUserData === 4) return "remoteWaiting";
    if (currentCampaign.hasStartScreen) return "classicStart";
    return "playing";
  };

  useEffect(() => {
    if (!campaign) return;

    const isRemote = campaign.collectUserData === 4;

    if (!isRemote) {
      if (hubRef.current) {
        hubRef.current.stop();
        hubRef.current = null;
      }

      if (remoteTimeoutRef.current) {
        clearTimeout(remoteTimeoutRef.current);
        remoteTimeoutRef.current = null;
      }
      return;
    }

    const hub = createGameHub(env!);
    hubRef.current = hub;

    hub.on("PlayerSubmitted", (payload: any) => {
      setRemotePlayer(payload);
      setStartPhase("playing");

      if (remoteTimeoutRef.current) {
        clearTimeout(remoteTimeoutRef.current);
      }

      remoteTimeoutRef.current = window.setTimeout(() => {
        if (hubRef.current && campaign) {
          hubRef.current.invoke("EndGame", campaign.id).catch(() => {});
        }
      }, 60000);
    });

    hub.on("GameLocked", () => {
    });

    hub.on("GameUnlocked", () => {
      setRemotePlayer(null);
      setStartPhase("remoteWaiting");

      if (remoteTimeoutRef.current) {
        clearTimeout(remoteTimeoutRef.current);
        remoteTimeoutRef.current = null;
      }
    });

    hub.on("SessionState", () => {
    });

    hub
      .start()
      .then(() => hub.invoke("RegisterDisplay", campaign.id))
      .catch(() => {
        Swal.fire("BaÄŸlantÄ± kurulamadÄ±", "LÃ¼tfen aÄŸÄ± kontrol edin.", "error");
      });

    return () => {
      hub.stop();
      hubRef.current = null;

      if (remoteTimeoutRef.current) {
        clearTimeout(remoteTimeoutRef.current);
        remoteTimeoutRef.current = null;
      }
    };
  }, [campaign]);

  const endRemoteGame = async () => {
    const hub = hubRef.current;
    if (hub && campaign) {
      try {
        await hub.invoke("EndGame", campaign.id);
      } catch {}
    }
  };

  const onRemotePlayStarted = async () => {
    if (remoteTimeoutRef.current) {
      clearTimeout(remoteTimeoutRef.current);
      remoteTimeoutRef.current = null;
    }
  };

  const screenType = campaign?.screenStyle.screenType;
  const screenSize: number = [1, 2, 5].includes(screenType ?? -1)
    ? 1080
    : screenType === 3
      ? 1920
      : screenType === 4
        ? 440
        : 1366;

  const bgFromTopVw = pxToVw(campaign?.screenStyle.screenCenterBgFromTop || 1300, screenSize);
  let background = "";
  let backgroundSize = "";
  const screenColor = campaign?.screenStyle.screenColor || "#E60805";

  if (campaign?.screenStyle.screenBg) {
    if (campaign?.screenStyle.screenCenterBg) {
      const screenBgUrl = resolveAssetUrl(campaign.screenStyle.screenBg);
      const screenCenterBgUrl = resolveAssetUrl(campaign.screenStyle.screenCenterBg);
      background = `url(${screenCenterBgUrl}) center ${bgFromTopVw}vw no-repeat, url(${screenBgUrl}) center center no-repeat, ${screenColor}`;
      backgroundSize = "auto, cover, auto";
    } else {
      const screenBgUrl = resolveAssetUrl(campaign.screenStyle.screenBg);
      background = `url(${screenBgUrl}) center center no-repeat, ${screenColor}`;
      backgroundSize = "cover, auto";
    }
  } else {
    backgroundSize = "auto";
    if (campaign?.screenStyle.screenCenterBg) {
      const screenCenterBgUrl = resolveAssetUrl(campaign.screenStyle.screenCenterBg);
      background = `url(${screenCenterBgUrl}) center ${bgFromTopVw}vw no-repeat, ${screenColor}`;
    } else {
      background = screenColor;
    }
  }

  const fetchCampaign = async () => {
    if (id) {
      try {
        const fetchedCampaign = await agent.Campaigns.details(id);

        if (fetchedCampaign.gameType === 1 && fetchedCampaign.wheel && fetchedCampaign.wheel.slices) {
          fetchedCampaign.wheel.slices.sort((a, b) => a.listOrder - b.listOrder);
          fetchedCampaign.wheel.slices.forEach((slice) => {
            slice.probability = slice.probability && parseFloat(slice.probability.toFixed(3));
          });
        } else if (fetchedCampaign.gameType === 1) {
          console.warn("Campaign wheel or slices are missing for spin wheel game");
        }

        setCampaign(fetchedCampaign);
        setRemotePlayer(null);
        setStartPhase(getInitialStartPhase(fetchedCampaign));
      } catch (error) {
        console.error("Failed to fetch campaign:", error);
      }
    } else {
      console.error("No campaign id provided");
    }
  };

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const handleStockDepletion = () => {
    fetchCampaign();
  };

  if (!campaign) {
    return <CenteredCard>Yükleniyor...</CenteredCard>;
  }

  const hasCustomStartVideo = !!resolveAssetUrl(campaign.startVideo);
  const startVideoSrc = hasCustomStartVideo
    ? resolveAssetUrl(campaign.startVideo)
    : `${process.env.PUBLIC_URL}/assets/video/tokenflex.mp4`;

  const modalConfig: ModalConfig = {
    modalText: campaign?.modalText ?? null,
    modalPassText: campaign?.modalPassText ?? null,
    modalBg: campaign?.modalBg ?? null,
    modalInnerBg: campaign?.modalInnerBg
      ? (campaign.modalInnerBg.startsWith("http") ? campaign.modalInnerBg : env + campaign.modalInnerBg)
      : null,
    modalBorderRadius: campaign?.modalBorderRadius ?? true,
    modalColor: campaign?.modalColor ?? "#fff",
    modalTextColor: campaign?.modalTextColor ?? "#545454",
  };

  const handleClassicStart = () => {
    if (startPhase === "classicStart") {
      setStartPhase("playing");
    }
  };

  const shouldShowStartScreen = startPhase !== "playing";
  const shouldMountGame = startPhase === "playing";
  const startScreenTitle = campaign.screenStyle.pageTitle || campaign.title;
  const startScreenMotto = campaign.screenStyle.motto || "Oyuna başlamak için ekrana dokunun.";
  const startScreenTextColor = campaign.screenStyle.textColor || "#fff";

  const renderStartScreen = () => {
    if (startPhase === "remoteWaiting") {
      return (
        <video
          className="remote-video"
          src={startVideoSrc}
          autoPlay
          muted
          playsInline
          loop
          style={{
            position: "fixed",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 9999,
          }}
        />
      );
    }

    if (hasCustomStartVideo) {
      return (
        <div
          className="classic-start-screen"
          role="button"
          tabIndex={0}
          onClick={handleClassicStart}
          onTouchStart={(event) => {
            event.preventDefault();
            handleClassicStart();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleClassicStart();
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            overflow: "hidden",
            cursor: "pointer",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            touchAction: "manipulation",
            background: "#000",
          }}
        >
          <video
            src={startVideoSrc}
            autoPlay
            muted
            playsInline
            loop
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>
      );
    }

    return (
      <div
        className="classic-start-screen"
        role="button"
        tabIndex={0}
        onClick={handleClassicStart}
        onTouchStart={(event) => {
          event.preventDefault();
          handleClassicStart();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleClassicStart();
          }
        }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          overflow: "hidden",
          color: startScreenTextColor,
          background,
          backgroundSize,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "clamp(24px, 4vw, 56px)",
          cursor: "pointer",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          touchAction: "manipulation",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(10, 18, 34, 0.2) 0%, rgba(10, 18, 34, 0.5) 100%)",
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            width: "min(920px, 100%)",
            maxWidth: "min(920px, 92vw)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "clamp(14px, 2vw, 24px)",
            padding: "clamp(22px, 3vw, 38px)",
            borderRadius: "clamp(22px, 3vw, 34px)",
            background: "rgba(255, 255, 255, 0.10)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 24px 80px rgba(0, 0, 0, 0.28)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
          }}
        >
          {campaign.screenStyle.logo && (
            <img
              className="screen-logo"
              src={resolveAssetUrl(campaign.screenStyle.logo)}
              alt={campaign.title}
              style={{
                maxWidth: "min(420px, 65vw)",
                width: "100%",
                height: "auto",
                objectFit: "contain",
                userSelect: "none",
              }}
            />
          )}
          {startScreenTitle && (
            <h1
              style={{
                margin: 0,
                fontSize: "clamp(2rem, 4vw, 4.25rem)",
                lineHeight: 1.1,
                textWrap: "balance",
                textShadow: "0 8px 30px rgba(0, 0, 0, 0.22)",
              }}
            >
              {startScreenTitle}
            </h1>
          )}
          <p
            style={{
              margin: 0,
              fontSize: "clamp(1rem, 2vw, 1.35rem)",
              lineHeight: 1.55,
              maxWidth: "42rem",
              opacity: 0.96,
            }}
          >
            {startScreenMotto}
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "0.6rem",
              marginTop: "clamp(8px, 1.8vw, 18px)",
            }}
          >
            <div
              style={{
                padding: "0.95rem 1.6rem",
                borderRadius: "999px",
                border: "2px solid rgba(255,255,255,0.85)",
                background: "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 100%)",
                fontSize: "clamp(1rem, 2.2vw, 1.4rem)",
                fontWeight: 700,
                letterSpacing: "0.02em",
                boxShadow: "0 12px 30px rgba(0, 0, 0, 0.18)",
              }}
            >
              Dokunarak Başlat
            </div>
            <span
              style={{
                fontSize: "clamp(0.92rem, 1.5vw, 1.05rem)",
                opacity: 0.9,
              }}
            >
              Oyuna başlamak için ekrana dokunun
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {campaign.screenStyle.titleFromTop != 0 && (`
          .game-screen h3 {
            margin-top: ${pxToVw(campaign.screenStyle.titleFromTop, screenSize)}vw;
            max-width: ${campaign.screenStyle.titleWidth}%;
          }
        `)}
        {campaign.screenStyle.titleFontSize != 0 && (`
          .game-screen h3 {
            font-size: ${pxToVw(campaign.screenStyle.titleFontSize, screenSize)}vw;
          }
        `)}
        {campaign.screenStyle.logoHeight != 0 && (`
          .game-screen .screen-logo {
            max-height: ${pxToVw(campaign.screenStyle.logoHeight, screenSize)}vw;
          }
        `)}
        {campaign.screenStyle.logoFromTop != 0 && (`
          .game-screen .screen-logo {
            margin-top: ${pxToVw(campaign.screenStyle.logoFromTop, screenSize)}vw;
          }
        `)}
        {campaign.gameType === 1 && campaign.screenStyle.gameFromTop != 0 && (`
          .wheel-container {
            top: ${campaign.screenStyle.gameFromTop}%;
          }
        `)}
        {campaign.gameType === 2 && campaign.screenStyle.gameFromTop != 0 && (`
          #puzzle-container {
            top: ${campaign.screenStyle.gameFromTop}% !important;
          }
        `)}
        {campaign.gameType === 6 && campaign.screenStyle.gameFromTop != 0 && (`
          #quiz {
            top: ${campaign.screenStyle.gameFromTop}% !important;
          }
        `)}
        {campaign.gameType === 7 && campaign.screenStyle.gameFromTop != 0 && (`
          #catchGame {
            top: ${campaign.screenStyle.gameFromTop}% !important;
          }
        `)}
        {campaign.customCSS}
      </style>
      {shouldShowStartScreen ? (
        renderStartScreen()
      ) : (
        <main
          className={`game-screen${campaign.gameType === 5 ? " game-screen--matchwin" : ""}`}
          style={{
            color: campaign.screenStyle.textColor || "#fff",
            background,
            backgroundSize,
          }}
        >
          {campaign.gameType !== 3 && campaign.screenStyle.logo && (
            <img
              className="screen-logo"
              src={resolveAssetUrl(campaign.screenStyle.logo)}
              alt={campaign.title}
            ></img>
          )}
          {campaign.gameType !== 3 && campaign.screenStyle.pageTitle && (
            <h3>{campaign.screenStyle.pageTitle}</h3>
          )}
          {campaign.gameType !== 3 && campaign.screenStyle.motto && (
            <p className="screen-motto">{campaign.screenStyle.motto}</p>
          )}
          {shouldMountGame && campaign.gameType === 1 && campaign.wheel && campaign.wheel.slices ? (
            <SpinWheel
              isActive={campaign.isActive}
              collectUserData={campaign.collectUserData}
              wheel={campaign.wheel}
              slices={campaign.wheel.slices}
              formState={campaign.formState}
              campaignId={campaign.id}
              onStockDepletion={handleStockDepletion}
              modalConfig={modalConfig}
              remote={
                campaign.collectUserData === 4
                  ? { enabled: true, player: remotePlayer, endGame: endRemoteGame }
                  : undefined
              }
              onRemotePlayStarted={campaign.collectUserData === 4 ? onRemotePlayStarted : undefined}
            />
          ) : shouldMountGame && campaign.gameType === 2 && campaign.puzzle ? (
            <Puzzle
              isActive={campaign.isActive}
              collectUserData={campaign.collectUserData}
              puzzle={campaign.puzzle}
              formState={campaign.formState}
              campaignId={campaign.id}
              campaignTitle={campaign.title}
              modalText={campaign.modalText}
              modalBg={campaign.modalBg}
            />
          ) : shouldMountGame && campaign.gameType === 3 && campaign.scratchWin ? (
            <ScratchAndWin
              isActive={campaign.isActive}
              collectUserData={campaign.collectUserData}
              scratchWin={campaign.scratchWin}
              scratchPrizes={campaign.scratchWin.scratchPrizes}
              screenStyle={campaign.screenStyle}
              formState={campaign.formState}
              campaignId={campaign.id}
              onStockDepletion={handleStockDepletion}
              modalConfig={modalConfig}
            />
          ) : shouldMountGame && campaign.gameType === 5 && campaign.matchWin ? (
            <MatchWin
              isActive={campaign.isActive}
              collectUserData={campaign.collectUserData}
              matchWin={campaign.matchWin}
              matchObjects={campaign.matchWin.matchObjects}
              formState={campaign.formState}
              modalConfig={modalConfig}
              campaignId={campaign.id}
              campaignTitle={campaign.title}
            />
          ) : shouldMountGame && campaign.gameType === 6 && campaign.quiz ? (
            <Quiz
              isActive={campaign.isActive}
              collectUserData={campaign.collectUserData}
              quiz={campaign.quiz}
              questions={campaign.quiz.questions}
              formState={campaign.formState}
              modalConfig={modalConfig}
              campaignId={campaign.id}
              campaignTitle={campaign.title}
              textColor={campaign.screenStyle.textColor}
            />
          ) : shouldMountGame && campaign.gameType === 7 && campaign.catchGame ? (
            <CatchGame
              isActive={campaign.isActive}
              collectUserData={campaign.collectUserData}
              catchGame={campaign.catchGame}
              formState={campaign.formState}
              modalConfig={modalConfig}
              campaignId={campaign.id}
              campaignTitle={campaign.title}
              textColor={campaign.screenStyle.textColor}
            />
          ) : shouldMountGame ? (
            <CenteredCard>Bu kampanya için oyun bulunamadı.</CenteredCard>
          ) : null}
        </main>
      )}
    </>
  );
}
